import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
export const DeviceConditionSchema = z.enum(['NEW', 'USED']);
export const DeviceVariantSchema = z.object({
    id: z.string(),
    deviceModelId: z.string(),
    /** e.g. "128GB Noir", "256GB Blanc" */
    name: z.string(),
    condition: DeviceConditionSchema,
    price: z.number(),
    weight: z.number(),
    color: z.string().nullish(),
    capacity: z.number().nullish(),
    isActive: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const DeviceModelSchema = z.object({
    id: z.string(),
    category: z.string(),
    brand: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    imageUrl: z.string().nullish(),
    isActive: z.boolean(),
    variants: z.array(DeviceVariantSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AccessorySchema = z.object({
    id: z.string(),
    category: z.string(),
    name: z.string(),
    price: z.number(),
    imageUrl: z.string().nullish(),
    weight: z.number(),
    isActive: z.boolean(),
    requiresCompatibility: z.boolean(),
    hasColors: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateDeviceModelBodySchema = z.object({
    category: z.string(),
    brand: z.string(),
    name: z.string(),
    description: z.string().optional(),
    imageUrl: z.string().optional(),
    isActive: z.boolean().optional(),
});
export const UpdateDeviceModelBodySchema = CreateDeviceModelBodySchema.partial();
const DeviceVariantBaseSchema = z.object({
    name: z.string(),
    condition: DeviceConditionSchema,
    price: z.number(),
    weight: z.number(),
    color: z.string().optional(),
    capacity: z.number().optional(),
    isActive: z.boolean().optional(),
});
export const CreateDeviceVariantBodySchema = DeviceVariantBaseSchema.extend({
    deviceModelId: z.string(),
});
export const UpdateDeviceVariantBodySchema = DeviceVariantBaseSchema.partial();
export const CreateAccessoryBodySchema = z.object({
    category: z.string().min(1, 'Catégorie requise'),
    name: z.string().min(1, 'Intitulé requis'),
    price: z.number().min(0, 'Prix doit être positif'),
    weight: z.number().min(0, 'Poids doit être positif'),
    imageUrl: z.string().url().optional().or(z.literal('')),
    isActive: z.boolean().default(true),
    requiresCompatibility: z.boolean().default(false),
    hasColors: z.boolean().default(false),
});
export const UpdateAccessoryBodySchema = z.object({
    category: z.string().min(1, 'Catégorie requise').optional(),
    name: z.string().min(1, 'Intitulé requis').optional(),
    price: z.number().min(0, 'Prix doit être positif').optional(),
    weight: z.number().min(0, 'Poids doit être positif').optional(),
    imageUrl: z.string().url().optional().or(z.literal('')),
    isActive: z.boolean().optional(),
    requiresCompatibility: z.boolean().optional(),
    hasColors: z.boolean().optional(),
});
const activeOnlyQuery = z.object({
    activeOnly: z.enum(['true', 'false']).optional(),
});
export const UploadCatalogImageBodySchema = z.object({
    category: z.enum(['accessories', 'devices']),
});
export const catalogAdminContract = c.router({
    /**
     * Multipart: the client sends FormData with the `image` file entry plus
     * the fields below; the file is handled by the server interceptor, not
     * the schema.
     */
    uploadCatalogImage: {
        method: 'POST',
        path: '/catalog/upload-image',
        contentType: 'multipart/form-data',
        body: UploadCatalogImageBodySchema,
        responses: { 201: z.object({ imageUrl: z.string() }) },
        summary: 'Upload a catalog image (admin)',
    },
    listDeviceModels: {
        method: 'GET',
        path: '/device-models',
        query: activeOnlyQuery,
        responses: { 200: z.array(DeviceModelSchema) },
        summary: 'List device models (with their variants)',
    },
    getDeviceModel: {
        method: 'GET',
        path: '/device-models/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        responses: { 200: DeviceModelSchema },
        summary: 'Get a device model by id',
    },
    createDeviceModel: {
        method: 'POST',
        path: '/device-models',
        body: CreateDeviceModelBodySchema,
        responses: { 201: DeviceModelSchema },
        summary: 'Create a device model',
    },
    updateDeviceModel: {
        method: 'PUT',
        path: '/device-models/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: UpdateDeviceModelBodySchema,
        responses: { 200: DeviceModelSchema },
        summary: 'Update a device model',
    },
    deleteDeviceModel: {
        method: 'DELETE',
        path: '/device-models/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: c.noBody(),
        responses: { 200: z.object({ success: z.boolean() }) },
        summary: 'Delete a device model',
    },
    listDeviceVariants: {
        method: 'GET',
        path: '/device-variants',
        query: activeOnlyQuery.extend({ deviceModelId: z.string().optional() }),
        responses: { 200: z.array(DeviceVariantSchema) },
        summary: 'List device variants',
    },
    createDeviceVariant: {
        method: 'POST',
        path: '/device-variants',
        body: CreateDeviceVariantBodySchema,
        responses: { 201: DeviceVariantSchema },
        summary: 'Create a device variant',
    },
    updateDeviceVariant: {
        method: 'PUT',
        path: '/device-variants/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: UpdateDeviceVariantBodySchema,
        responses: { 200: DeviceVariantSchema },
        summary: 'Update a device variant',
    },
    deleteDeviceVariant: {
        method: 'DELETE',
        path: '/device-variants/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: c.noBody(),
        responses: { 200: z.object({ success: z.boolean() }) },
        summary: 'Delete a device variant',
    },
    listAccessories: {
        method: 'GET',
        path: '/accessories',
        query: activeOnlyQuery,
        responses: { 200: z.array(AccessorySchema) },
        summary: 'List accessories',
    },
    createAccessory: {
        method: 'POST',
        path: '/accessories',
        body: CreateAccessoryBodySchema,
        responses: { 201: AccessorySchema },
        summary: 'Create an accessory',
    },
    updateAccessory: {
        method: 'PUT',
        path: '/accessories/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: UpdateAccessoryBodySchema,
        responses: { 200: AccessorySchema },
        summary: 'Update an accessory',
    },
    deleteAccessory: {
        method: 'DELETE',
        path: '/accessories/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: c.noBody(),
        responses: { 200: z.object({ success: z.boolean() }) },
        summary: 'Delete an accessory',
    },
});
